import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  FaTint, FaFlask, FaHome, FaTrash, FaCheck, FaBuilding, FaArrowRight, FaUser,
} from "react-icons/fa";
import Header from "../Components/Header";
import Footer from "../Components/Footer";
import EmptyCartState from "../Components/States/EmptyCartState";
import OfflineState from "../Components/States/OfflineState";
import LoadingState from "../Components/States/LoadingState";
import AddressFormModal from "../Components/AddressFormModal";
import { isNetworkError } from "../utils/network";
import { getCart, deleteFromCart } from "../api/cart.api";
import { getWallet } from "../api/staff.api";
import { getAddresses } from "../api/address.api";
import { getFamilyMembers } from "../api/family.api";
import { loadStaffDiagnosticCenters, getDiagnosticSlots } from "../api/diagnostics.api";
import { createDiagnosticBookings } from "../api/bookings.api";
import { submitBookingWithPayment } from "../utils/bookingPayment";
import { apiErrorMessage } from "../utils/apiError";
import {
  getStaffId, getStaffName, STORAGE_KEYS,
} from "../utils/storage";
import { cartLineFromApi, diagnosticMatchesItems, formatPrice, ITEM_TYPE } from "../utils/labTests";
import { toast } from "../Components/Toast";
import {
  getLabWalletBalance,
  getCategoryWalletAllocation,
  formatWalletAmount,
} from "../utils/wallet";

const toYMD = (d) => {
  const x = d instanceof Date ? d : new Date(d);
  return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
};

const nextDays = (count = 14) =>
  Array.from({ length: count }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() + i + 1);
    return toYMD(d);
  });

const formatAddress = (a) =>
  [a.street, a.city, a.state, a.postalCode].filter(Boolean).join(", ");

const dayCardLabel = (ymd) => {
  const d = new Date(ymd + "T00:00:00");
  const weekday = d.toLocaleDateString("en-US", { weekday: "short" }).toUpperCase();
  const day = d.getDate();
  return { weekday, day };
};

const isCorporateCovered = (item) => Number(item.totalPayable) === 0;

const CartPage = () => {
  const navigate = useNavigate();
  const staffId = getStaffId();
  const staffName = getStaffName() || "Myself";

  const [cartItems, setCartItems] = useState([]);
  const [grandTotal, setGrandTotal] = useState(0);
  const [walletData, setWalletData] = useState(null);
  const [addresses, setAddresses] = useState([]);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [diagnostics, setDiagnostics] = useState([]);
  const [selectedLab, setSelectedLab] = useState(null);
  const [selectedMemberId, setSelectedMemberId] = useState("");
  const [selectedAddressId, setSelectedAddressId] = useState("");
  const [serviceType, setServiceType] = useState("Home Collection");
  const [selectedDate, setSelectedDate] = useState("");
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [networkError, setNetworkError] = useState(false);

  const [dateOptions] = useState(() => nextDays(14));

  const lineItems = useMemo(() => cartItems.map(cartLineFromApi), [cartItems]);
  const itemIds = useMemo(() => lineItems.map((i) => i.id), [lineItems]);

  const subtotal = grandTotal || cartItems.reduce((s, i) => s + (Number(i.totalPayable) || 0), 0);
  const walletBal = getLabWalletBalance(walletData);
  const categoryAlloc = getCategoryWalletAllocation(walletData, "lab");
  const walletUse = Math.min(subtotal, walletBal);
  const canPayWithWallet = walletBal >= subtotal;
  const shortfall = Math.max(0, subtotal - walletBal);
  const fastingNote = lineItems.some((i) => i.fastingRequired);
  const needsHomeCollection = cartItems.some((i) => i.homeCollectionAvailable);

  const filteredLabs = useMemo(() => {
    return diagnostics.filter((d) => diagnosticMatchesItems(d, itemIds));
  }, [diagnostics, itemIds]);

  const loadCart = useCallback(() => {
    if (!staffId) return Promise.resolve();
    return getCart(staffId)
      .then((response) => {
        const items = response.data?.items || [];
        setCartItems(items);
        setGrandTotal(Number(response.data?.grandTotal) || 0);
        if (items.some((i) => i.homeCollectionAvailable)) {
          setServiceType("Home Collection");
        } else {
          setServiceType("Center Visit");
        }
      });
  }, [staffId]);

  const loadPageData = useCallback(() => {
    if (!staffId) {
      setError("No staffId found in localStorage");
      setLoading(false);
      return;
    }

    setLoading(true);
    setNetworkError(false);
    setError("");

    Promise.all([
      loadCart(),
      getWallet(staffId).catch(() => ({ data: null })),
      getAddresses(staffId).catch(() => ({ data: { addresses: [] } })),
      getFamilyMembers(staffId).catch(() => ({ data: { family_members: [] } })),
      loadStaffDiagnosticCenters().catch(() => ({ data: { data: [] } })),
    ])
      .then(([, walletRes, addrRes, famRes, diagRes]) => {
        setWalletData(walletRes.data);
        const addrs = addrRes.data?.addresses || [];
        const members = famRes.data?.family_members || [];
        setAddresses(addrs);
        setFamilyMembers(members);
        setDiagnostics(diagRes.data?.data || []);

        const self = members.find((m) => /self/i.test(m.relation || ""));
        if (self) setSelectedMemberId(self._id);
        else if (members[0]) setSelectedMemberId(members[0]._id);

        if (addrs[0]) setSelectedAddressId(addrs[0]._id);
      })
      .catch((err) => {
        if (isNetworkError(err)) setNetworkError(true);
        else setError("Error loading cart data");
      })
      .finally(() => setLoading(false));
  }, [staffId, loadCart]);

  useEffect(() => {
    loadPageData();
  }, [loadPageData]);

  useEffect(() => {
    if (!filteredLabs.length || !itemIds.length) {
      setSelectedLab(null);
      return;
    }

    let storedIds = [];
    try {
      storedIds = JSON.parse(localStorage.getItem(STORAGE_KEYS.cartDiagnosticIds) || "[]");
    } catch {
      storedIds = [];
    }

    const preferred = storedIds.length
      ? filteredLabs.find((l) => storedIds.includes(l._id))
      : null;
    setSelectedLab(preferred || filteredLabs[0]);
  }, [filteredLabs, itemIds.length]);

  const fetchSlots = useCallback((labId, date, type) => {
    if (!labId || !date) return;
    setLoadingSlots(true);
    setSelectedSlot(null);
    getDiagnosticSlots(labId, date, type)
      .then((res) => setAvailableSlots((res.data?.slots || []).filter((s) => !s.isBooked && !s.isExpired)))
      .catch(() => setAvailableSlots([]))
      .finally(() => setLoadingSlots(false));
  }, []);

  const handleDateSelect = (ymd) => {
    setSelectedDate(ymd);
    if (selectedLab) fetchSlots(selectedLab._id, ymd, serviceType);
  };

  useEffect(() => {
    if (selectedLab && selectedDate) {
      fetchSlots(selectedLab._id, selectedDate, serviceType);
    }
  }, [serviceType, selectedLab, selectedDate, fetchSlots]);

  const handleDeleteItem = (itemId) => {
    if (!staffId) return;
    deleteFromCart(staffId, { itemId })
      .then((response) => {
        const items = response.data?.items || [];
        setCartItems(items);
        setGrandTotal(Number(response.data?.grandTotal) || 0);
        if (items.length === 0) {
          localStorage.removeItem(STORAGE_KEYS.cartDiagnosticIds);
          setSelectedLab(null);
          setSelectedDate("");
          setSelectedSlot(null);
        }
      })
      .catch(() => setError("Error removing item from cart"));
  };

  const handleAddressSaved = (addr) => {
    setAddresses((prev) => [...prev, addr]);
    setSelectedAddressId(addr._id);
    setError("");
  };

  const canConfirm =
    Boolean(selectedLab) &&
    Boolean(selectedMemberId) &&
    Boolean(selectedDate) &&
    Boolean(selectedSlot) &&
    (serviceType !== "Home Collection" || Boolean(selectedAddressId));

  const patient = familyMembers.find((m) => m._id === selectedMemberId);
  const address = addresses.find((a) => a._id === selectedAddressId);

  const bookingBase = () => ({
    familyMemberId: selectedMemberId,
    diagnosticId: selectedLab._id,
    serviceType,
    date: selectedDate,
    timeSlot: selectedSlot.timeSlot,
    addressId: serviceType === "Home Collection" && address?._id ? address._id : null,
  });

  const goConfirmed = (bookingResponse) => {
    toast.success("Lab test order placed successfully.");
    localStorage.removeItem(STORAGE_KEYS.cartDiagnosticIds);
    navigate("/lab-tests/confirmed", {
      replace: true,
      state: {
        lineItems,
        patient: { familyMemberId: selectedMemberId, name: patient?.fullName || staffName },
        diagnostic: selectedLab,
        serviceType,
        selectedDate,
        selectedTime: selectedSlot.timeSlot,
        selectedSlot,
        booking: bookingResponse,
        walletUsed: walletUse,
        amountPaid: subtotal,
        fastingNote,
      },
    });
  };

  const handleConfirm = async () => {
    if (!canConfirm || processing) return;
    setError("");
    setProcessing(true);
    try {
      const data = await submitBookingWithPayment({
        post: (payload) => createDiagnosticBookings(staffId, payload),
        body: bookingBase(),
        description: `Lab tests at ${selectedLab?.name || "diagnostic center"}`,
        refreshCart: true,
      });
      goConfirmed(data);
    } catch (err) {
      const msg = apiErrorMessage(err, "Booking failed. Please try again.");
      if (msg === "Payment cancelled") {
        toast.error("Payment cancelled");
      } else {
        setError(msg);
        toast.error(msg);
      }
    } finally {
      setProcessing(false);
    }
  };

  const displaySlots = availableSlots.slice(0, 4);
  const hasCart = !networkError && !loading && cartItems.length > 0;

  const confirmButton = (className = "") => (
    <button
      type="button"
      onClick={handleConfirm}
      disabled={!canConfirm || processing}
      className={`py-3.5 px-5 rounded-xl bg-primary text-white font-semibold flex items-center justify-center gap-2 hover:bg-primary-container disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {processing ? "Processing..." : (
        <>
          Confirm Booking
          <FaArrowRight size={14} />
        </>
      )}
    </button>
  );

  return (
    <div className="bg-surface-low min-h-screen flex flex-col">
      <Header />
      <div className={`flex flex-col min-h-screen flex-1 ${hasCart ? "pb-28 lg:pb-0" : "pb-16 lg:pb-0"}`}>
        <main className="flex-1 max-w-7xl mx-auto w-full px-3 sm:px-6 py-4 sm:py-6">
          <h1 className="text-2xl sm:text-3xl font-bold text-on-primary-fixed-var mb-1">Your Cart</h1>
          <p className="text-on-surface-variant mb-6">
            Review your selected health tests before confirming logistics.
          </p>

          {error && (
            <p className="text-error text-sm mb-4 bg-error-container/30 border border-error/20 rounded-lg px-4 py-2">
              {error}
            </p>
          )}

          {networkError && (
            <OfflineState onRetry={loadPageData} />
          )}

          {!networkError && loading && (
            <LoadingState branded message="Loading cart…" />
          )}

          {!networkError && !loading && cartItems.length === 0 && (
            <EmptyCartState />
          )}

          {hasCart && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
              {/* Left column — items + wallet */}
              <div className="space-y-3 sm:space-y-4">
                {cartItems.map((item) => {
                  const covered = isCorporateCovered(item);
                  return (
                    <div
                      key={item.itemId}
                      className="bg-surface-lowest border border-surface-container rounded-2xl p-4 shadow-sm"
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className={`shrink-0 w-11 h-11 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                            covered ? "bg-primary text-white" : "bg-surface-container text-on-surface-variant"
                          }`}
                        >
                          {covered ? <FaTint size={18} /> : <FaFlask size={18} />}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="font-bold text-on-primary-fixed-var text-sm sm:text-base leading-snug break-words">
                              <Link
                                to="/lab-tests/book"
                                state={{
                                  item: cartLineFromApi(item),
                                  itemType: ITEM_TYPE.test,
                                }}
                                className="hover:text-primary hover:underline transition-colors"
                              >
                                {item.title}
                              </Link>
                            </h3>
                            <button
                              type="button"
                              onClick={() => handleDeleteItem(item.itemId)}
                              className="shrink-0 text-error hover:text-error/80 p-1.5 -mr-1"
                              aria-label="Remove item"
                            >
                              <FaTrash size={14} />
                            </button>
                          </div>
                          <p className="text-xs sm:text-sm text-on-surface-variant mt-1">
                            {item.fastingRequired
                              ? "Fasting required (8–12 hours)."
                              : "Standard blood draw."}
                          </p>
                          {covered ? (
                            <span className="inline-flex items-center gap-1.5 mt-2 text-[10px] sm:text-xs font-semibold text-primary bg-primary-fixed/30 border border-primary-fixed-dim px-2.5 py-1 rounded-md">
                              <FaBuilding size={10} />
                              CORPORATE COVERED
                            </span>
                          ) : (
                            <p className="font-semibold text-on-primary-fixed-var mt-2 text-sm">
                              {formatPrice(item.totalPayable)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                <div className="card p-4 sm:p-5 space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-on-surface-variant">Available corporate balance</span>
                    <span className="font-bold text-primary">₹{formatWalletAmount(walletBal)}</span>
                  </div>
                  {categoryAlloc != null && categoryAlloc !== walletBal && (
                    <p className="text-xs text-on-surface-variant">
                      Category allocation: ₹{formatWalletAmount(categoryAlloc)} (total balance used for booking)
                    </p>
                  )}
                  <div className="flex justify-between text-sm">
                    <span className="text-on-surface-variant">Subtotal</span>
                    <span className="text-on-primary-fixed-var">{formatPrice(subtotal)}</span>
                  </div>
                  {walletUse > 0 && (
                    <div className="flex justify-between text-sm">
                      <span className="text-on-surface-variant">Wallet Applied</span>
                      <span className="text-primary">-{formatPrice(walletUse)}</span>
                    </div>
                  )}
                  <div className="flex justify-between pt-2 border-t border-surface-container">
                    <span className="font-bold text-on-primary-fixed-var">Paid from wallet</span>
                    <span className="font-bold text-primary">{formatPrice(walletUse)}</span>
                  </div>
                  {!canPayWithWallet && (
                    <p className="text-sm text-error pt-2">
                      Shortfall: {formatPrice(shortfall)}.{" "}
                      <Link to="/wallet" className="underline font-medium">View wallet</Link>
                      <span className="block mt-1 text-on-surface-variant">
                        Remaining {formatPrice(shortfall)} will be paid online.
                      </span>
                    </p>
                  )}
                </div>
              </div>

              {/* Right column — logistics */}
              <div className="border border-surface-container lg:border-2 rounded-2xl p-4 sm:p-5 lg:p-6 bg-surface-lowest h-fit">
                {(selectedLab || patient) && (
                  <div className="flex flex-wrap gap-2 mb-4">
                    {selectedLab && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-on-primary-fixed-var bg-surface-low px-2.5 py-1.5 rounded-full border border-surface-container max-w-full">
                        <FaFlask size={10} className="shrink-0 text-primary" />
                        <span className="truncate">{selectedLab.name || selectedLab.diagnosticName || "Lab selected"}</span>
                      </span>
                    )}
                    {patient && (
                      <span className="inline-flex items-center gap-1.5 text-xs font-medium text-on-primary-fixed-var bg-surface-low px-2.5 py-1.5 rounded-full border border-surface-container">
                        <FaUser size={10} className="shrink-0 text-primary" />
                        {patient.fullName || staffName}
                      </span>
                    )}
                  </div>
                )}

                <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-2">
                  Collection Method
                </p>
                {needsHomeCollection ? (
                  <div className="flex rounded-lg overflow-hidden border border-surface-container mb-6">
                    {[
                      { label: "Home Collection", value: "Home Collection" },
                      { label: "Lab Visit", value: "Center Visit" },
                    ].map(({ label, value }) => (
                      <button
                        key={value}
                        type="button"
                        onClick={() => setServiceType(value)}
                        className={`flex-1 py-2.5 text-sm font-semibold transition-colors ${
                          serviceType === value
                            ? "bg-primary text-white"
                            : "bg-surface-lowest text-on-surface-variant hover:bg-surface-low"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-on-surface-variant mb-6">Lab visit only for selected items.</p>
                )}

                {serviceType === "Home Collection" && (
                  <div className="mb-6">
                    <div className="flex items-center justify-between mb-3">
                      <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide">
                        Service Address
                      </p>
                      <button
                        type="button"
                        onClick={() => setAddressModalOpen(true)}
                        className="text-xs font-bold text-primary uppercase tracking-wide"
                      >
                        + Add New
                      </button>
                    </div>

                    <div className="space-y-2">
                      {addresses.map((a) => (
                        <button
                          key={a._id}
                          type="button"
                          onClick={() => setSelectedAddressId(a._id)}
                          className={`w-full text-left p-4 rounded-xl border-2 transition-colors ${
                            selectedAddressId === a._id
                              ? "border-primary bg-primary-fixed/10"
                              : "border-surface-container hover:border-primary/40"
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex items-start gap-3 min-w-0">
                              <FaHome className="text-primary mt-0.5 shrink-0" size={16} />
                              <div>
                                <p className="font-semibold text-on-primary-fixed-var text-sm">
                                  {a.addressType || "Home"}
                                </p>
                                <p className="text-sm text-on-surface-variant mt-0.5">{formatAddress(a)}</p>
                              </div>
                            </div>
                            {selectedAddressId === a._id && (
                              <FaCheck className="text-primary shrink-0" size={14} />
                            )}
                          </div>
                        </button>
                      ))}
                      {!addresses.length && (
                        <p className="text-sm text-on-surface-variant">No addresses saved. Add one to continue.</p>
                      )}
                    </div>
                  </div>
                )}

                {!selectedLab && (
                  <p className="text-sm text-error mb-4">
                    No diagnostic center found for your cart items.
                  </p>
                )}

                <p className="text-xs font-semibold text-on-surface-variant uppercase tracking-wide mb-3">
                  Select Date &amp; Time
                </p>

                <div className="flex gap-2 overflow-x-auto pb-2 mb-4 snap-x snap-mandatory [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                  {dateOptions.map((d) => {
                    const { weekday, day } = dayCardLabel(d);
                    return (
                      <button
                        key={d}
                        type="button"
                        onClick={() => handleDateSelect(d)}
                        disabled={!selectedLab}
                        className={`shrink-0 snap-start w-16 py-3 rounded-xl border text-center disabled:opacity-40 disabled:cursor-not-allowed ${
                          selectedDate === d
                            ? "border-primary bg-primary text-white"
                            : "border-surface-container bg-surface-lowest text-on-primary-fixed-var"
                        }`}
                      >
                        <span className="block text-[10px] font-semibold">{weekday}</span>
                        <span className="block text-lg font-bold leading-tight">{day}</span>
                      </button>
                    );
                  })}
                </div>

                {loadingSlots && (
                  <LoadingState size="sm" message="Loading slots…" className="py-3 mb-3" />
                )}

                {selectedDate && !loadingSlots && displaySlots.length > 0 && (
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-6">
                    {displaySlots.map((s) => (
                      <button
                        key={s.timeSlot}
                        type="button"
                        onClick={() => setSelectedSlot(s)}
                        className={`min-h-[44px] py-2.5 rounded-lg border text-sm font-semibold ${
                          selectedSlot?.timeSlot === s.timeSlot
                            ? "border-primary bg-primary text-white"
                            : "border-primary text-primary bg-surface-lowest hover:bg-primary-fixed/20"
                        }`}
                      >
                        {s.timeSlot}
                      </button>
                    ))}
                  </div>
                )}

                {selectedDate && !loadingSlots && !displaySlots.length && selectedLab && (
                  <p className="text-sm text-on-surface-variant mb-6">No slots available for this date.</p>
                )}

                <div className="hidden lg:block">
                  {confirmButton("w-full")}
                </div>
              </div>
            </div>
          )}
        </main>

        {hasCart && (
          <div className="lg:hidden fixed bottom-16 inset-x-0 z-30 bg-surface-lowest border-t border-surface-container px-4 py-3 shadow-[0_-4px_12px_rgba(0,0,0,0.06)]">
            <div className="flex items-center justify-between gap-3 max-w-7xl mx-auto">
              <div className="min-w-0">
                <p className="text-xs text-on-surface-variant">Paid from wallet</p>
                <p className="font-bold text-primary">{formatPrice(walletUse)}</p>
              </div>
              {confirmButton("shrink-0 px-4 sm:px-6")}
            </div>
          </div>
        )}

        <Footer />
      </div>

      <AddressFormModal
        open={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        staffId={staffId}
        existingAddresses={addresses}
        onSaved={handleAddressSaved}
      />
    </div>
  );
};

export default CartPage;
